import {
    ActionReducerMapBuilder,
    Draft,
    SerializedError,
    createAsyncThunk,
    createSlice,
} from '@reduxjs/toolkit';
import { IAction, SliceProps } from '../store';
import { urlParamReplicaId, urlProjectWorkflowInitial } from '../../Constants';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectWorkflowInitialState = {
    data: null,
    loading: false,
    error: null,
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    getProjectWorkflowInitial: async (config: {
        replicaId: string;
        universalId: string;
    }): Promise<string> => {
        try {
            const { replicaId, universalId } = config;

            const request: RequestInit = {
                method: 'GET',
                headers: {
                    workflowId: universalId,
                },
            };

            const url = urlProjectWorkflowInitial.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(response.text())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectWorkflowInitialState>) => {
        /**
         * Reducers for the 'getProjectWorkflowInitial' thunk.
         */
        builder.addCase(
            getProjectWorkflowInitial.pending,
            (state: Draft<IProjectWorkflowInitialState>) => {
                state.loading = true;
                state.error = null;
            },
        );

        builder.addCase(
            getProjectWorkflowInitial.fulfilled,
            (state: Draft<IProjectWorkflowInitialState>, action: IAction<string>) => {
                state.data = action.payload;

                state.loading = false;
                state.error = null;
            },
        );

        builder.addCase(
            getProjectWorkflowInitial.rejected,
            (state: Draft<IProjectWorkflowInitialState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const getProjectWorkflowInitial = createAsyncThunk(
    `${urlProjectWorkflowInitial}`,
    thunks.getProjectWorkflowInitial,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectWorkflowInitialState extends SliceProps {
    data: string;
}

/**
 * Create the redux slice.
 */
export const projectWorkflowInitialSlice = createSlice({
    name: 'projectWorkflowInitial',
    extraReducers: thunks._reducers,
    initialState,
    reducers: {},
});

/**
 * Export slice reducer as default.
 */
export default projectWorkflowInitialSlice.reducer;
